import React from 'react'
import styles from './RegionalEmbedlet.scss'
import blackTheme from '../blackEmbedTheme.scss'
import cx from 'classnames'
import { Maplet } from 'common/Maplet'
import { POIMarker } from 'common/Maplet/POIMarker'
import * as L from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-markercluster'
import { FeatureDetails } from '../FeatureDetails'
import { PoiBar } from '../PoiBar'
import { base } from 'lib/styleguide/base.scss'
import { icon, divIcon } from 'leaflet'
import pinIconURL from 'assets/images/drop-pin-shadow.svg'
import blackFlagIconURL from 'assets/images/black-flag.png'
import { iconTypes } from 'common/Map/Marker/iconTypes'
const startMarker = icon(iconTypes.start)

// todo switch this to SVG once rwgps-ui#870 is resolved
const pinIcon = icon({
  iconUrl: pinIconURL,
  iconSize: [35, 52],
  iconAnchor: [17, 52]
})

const flagIcon = icon({
  iconUrl: blackFlagIconURL,
  iconSize: [20, 20],
  iconAnchor: [3, 20]
})

// pulled from NYC transit colors
export const ROUTE_COLORS = [
  '#FC4141',
  '#1DAFE6',
  '#1FAD51',
  '#FCA347',
  '#FDC943',
  '#972F91'
]

const POI_BAR_HEIGHT = 45

const equalFeatures = (a, b) => (
  a && b &&
  a.id === b.id &&
  a.type === b.type
)

const blackFlagIconMarker = function (cluster) {
  return divIcon({
    html: '<div class="' + styles.blackFlagCluster + '"><img class="' + styles.flagImage + '" src="' + blackFlagIconURL + '" /><span class="' + styles.count + '">' + cluster.getChildCount() + '</span></div>',
    className: 'marker-cluster-blackflag',
    iconSize: [40, 40],
    iconAnchor: [0, 40]
  })
}

const CLUSTER_ZOOM_THRESHOLD = 4

export class RegionalEmbedlet extends React.PureComponent {
  static defaultProps = {
    pois: []
  }

  constructor (props) {
    super(props)
    this.state = {
      enabledPoiTags: props.enabledPoiTags,
      zoom: 0
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const { enabledPoiTags } = this.props

    if (prevProps.enabledPoiTags !== enabledPoiTags) {
      this.setState({ enabledPoiTags })
    }
  }

  styleFeature = (feature) => {
    const { selectedFeature, hoveredFeature } = this.state
    const { rso, details, clusterTheme } = this.props
    const index = rso.route_ids.indexOf(feature.properties.id)
    if (index === -1) return {}
    const isSelected = equalFeatures(selectedFeature, feature.properties)
    const isHover = equalFeatures(hoveredFeature, feature.properties)

    const selectedColorOverride = (clusterTheme === 'black' && '#6187c2')
    const colorOverride = (isSelected && selectedColorOverride) || (details.colors && details.colors.Route && details.colors.Route[feature.properties.id])

    const opacity = hoveredFeature
      ? (isHover ? 1 : 0.5)
      : 1
    return {
      color: colorOverride || ROUTE_COLORS[index % ROUTE_COLORS.length],
      opacity: opacity,
      weight: isSelected ? 4 : 3,
      className: styles.debounceOpacity
    }
  }

  styleOutlines = (feature) => {
    const { selectedFeature, hoveredFeature } = this.state
    const isSelected = equalFeatures(selectedFeature, feature.properties)
    const isHover = equalFeatures(hoveredFeature, feature.properties)
    const opacity = hoveredFeature
      ? (isHover ? 1 : 0.5)
      : 1
    return {
      color: '#FFFFFF',
      opacity: opacity,
      weight: isSelected ? 10 : 6,
      className: styles.debounceOpacity
    }
  }

  styleClickTargets = () => {
    return {
      color: 'transparent', // rgba(255, 0, 0, 0.2) for debugging
      weight: 30 // this may cause issues in very dense regional embeds
    }
  }

  attachEventHandlers = (feature, layer) => {
    const serialized = { id: feature.properties.id, type: feature.properties.type }
    layer.on('click', () => this.setState({
      selectedFeature: serialized
    }))
    layer.on('mouseover', () => {
      this.setState({
        hoveredFeature: serialized
      })
    })
    layer.on('mouseout', () => {
      // if something else already grabbed focus, ignore the event
      if (this.state.hoveredFeature && (this.state.hoveredFeature.id !== serialized.id || this.state.hoveredFeature.type !== serialized.type)) {
        return
      }
      this.setState({
        hoveredFeature: null
      })
    })
  }

  filterSelected = (feature) => {
    const { selectedFeature } = this.state
    return equalFeatures(selectedFeature, feature.properties)
  }

  onSelectPOI = (poi) => {
    this.setState({ selectedFeature: { id: poi.id, type: 'poi' } })
  }

  deselectFeature = () => {
    this.setState({ selectedFeature: null })
  }

  onTogglePoiTag = (tag) => {
    const { enabledPoiTags } = this.state
    const withToggle = enabledPoiTags.has(tag)
      ? enabledPoiTags.remove(tag)
      : enabledPoiTags.add(tag)
    this.setState({ enabledPoiTags: withToggle })
  }

  getAsset = (feature) => {
    const { assets, pois } = this.props
    if (feature.type === 'route' || feature.type === 'trip') {
      return assets[feature.type][feature.id]
    }
    if (feature.type === 'poi') {
      return pois.find(p => p.id === feature.id)
    }
  }

  getBounds = () => {
    return this._maplet._map.leafletElement.getBounds().toBBoxString()
  }

  // This marker click handler is only attached to markers.
  // These markers look like clusters of size 1, thanks to the 'singleMarkerMode' option in <MarkerClusterGroup />
  // Since the handler is attached via the <MarkerCLusterGroup /> component, once we hit the zoom threshold, this handler won't get clicked because then you are clicking on real markers
  // 5 is picked for the zoom because it is 1 farther in than 4. See <Maplet />:shouldShowClusters()
  onMarkerClick = (m) => {
    const minZoom = CLUSTER_ZOOM_THRESHOLD + 1
    if (m._map.getZoom() < minZoom) {
      m._map.setView(m._latlng, minZoom)
    } else {
      m._map.setView(m._latlng, m._map.getZoom())
    }
  }

  shouldShowClusters = () => {
    // TODO: instead of storing the zoom in state, can I access via ref or something the map? and ask for its zoom level?
    const { zoom } = this.state
    const { useClusters, onlyShowClusters } = this.props
    return useClusters && (onlyShowClusters || zoom <= CLUSTER_ZOOM_THRESHOLD)
  }

  onShowClusters = () => (
    this.deselectFeature()
  )

  onZoomChange = (zoom) => {
    const { zoomLimit } = this.props
    if (zoomLimit && zoom > zoomLimit) { return }
    this.setState({ zoom })
  }

  // loading state?
  // maybe serialize the default bounds for the map in the embed code?
  render () {
    const { selectedFeature, hoveredFeature, enabledPoiTags } = this.state
    const {
      editing,
      metricUnits,
      showUnitConversion,
      overlay,
      poiBar,
      useClusters,
      scrollWheelZoom,
      showDetailsOnHover,
      hideFeatureLink,
      noLinks,
      zoomLimit,
      clusterTheme,
      showStartMarkers,
      initialBounds,
      geoJSON,
      rso,
      pois,
      details,
      hidePoweredBy
    } = this.props

    const mapHeight = poiBar
      ? `calc(100% - ${POI_BAR_HEIGHT}px`
      : '100%'
    const poiSelected = selectedFeature && selectedFeature.type === 'poi'
    const hasSelectedTag = poi => poi.tagNames.find(t => enabledPoiTags.has(t))
    const poiFilter = poiSelected
      ? poi => poi.id !== selectedFeature.id && hasSelectedTag(poi)
      : hasSelectedTag

    const startPoints = (useClusters || showStartMarkers) && rso && rso.start_points.map((sp) => ({ key: sp.id, latlng: sp.point, type: sp.type }))

    const markers = startPoints && startPoints.map((m) => {
      const mouseEvents = {
        onClick: () => this.setState({ selectedFeature: { id: m.key, type: m.type } }),
        onMouseOver: () => this.setState({ hoveredFeature: { id: m.key, type: m.type } }),
        onMouseOut: () => {
          if (this.state.hoveredFeature && (this.state.hoveredFeature.id === m.key && this.state.hoveredFeature.type === m.type)) {
            this.setState({ hoveredFeature: null })
          }
        }
      }
      return <L.Marker
        position={m.latlng}
        key={m.key}
        icon={clusterTheme === 'black' ? flagIcon : startMarker}
        {...mouseEvents} />
    })

    const clusterGroup = startPoints && this.shouldShowClusters() && (
      <MarkerClusterGroup
        iconCreateFunction={clusterTheme === 'black' && blackFlagIconMarker}
        showCoverageOnHover={false}
        zoomToBoundsOnClick
        spiderfyOnMaxZoom={false}
        singleMarkerMode
        maxClusterRadius={100}
        onMarkerClick={this.onMarkerClick}>
        {markers}
      </MarkerClusterGroup>
    )

    return (
      <div className={cx(base, styles.RegionalEmbedlet, useClusters && clusterTheme === 'black' && blackTheme.RegionalEmbed)}>
        <div className={styles.mapContainer} style={{ height: mapHeight }}>
          <Maplet
            ref={r => { this._maplet = r }}
            initialBounds={initialBounds}
            overwriteMaxZoom={zoomLimit}
            overlay={overlay}
            zoomPosition='topleft'
            onZoomChange={this.onZoomChange}
            scrollWheelZoom={!!scrollWheelZoom}
            attributionPosition='bottomright'
            clusterGroup={clusterGroup}
            onShowClusters={this.onShowClusters}
            markers={markers}
            onMapClick={this.deselectFeature}>
            {rso && !clusterGroup && <L.LayerGroup>
              {geoJSON && <React.Fragment>
                {/* outlines */}
                <L.GeoJSON
                  data={geoJSON}
                  style={this.styleOutlines}
                  interactive={false} />
                {/* actual route lines */}
                <L.GeoJSON
                  data={geoJSON}
                  style={this.styleFeature}
                  interactive={false} />
                {/* selected route line */}
                {selectedFeature && <L.GeoJSON
                  key={selectedFeature.type + selectedFeature.id}
                  data={geoJSON}
                  style={this.styleFeature}
                  filter={this.filterSelected}
                  interactive={false} />}
                {/* click targets */}
                <L.GeoJSON
                  data={geoJSON}
                  style={this.styleClickTargets}
                  bubblingMouseEvents={false}
                  onEachFeature={this.attachEventHandlers} />
              </React.Fragment>}
              {/* POIs - need to fix naming issues */}
              {pois.filter(poiFilter).map(p => <POIMarker poi={p} opacity={poiSelected ? 0.5 : 1.0} key={p.id} onClick={() => this.onSelectPOI(p)} />)}
              {poiSelected &&
                <L.Marker icon={pinIcon} zIndexOffset={999} position={pois.find(p => p.id === selectedFeature.id)} />}
            </L.LayerGroup>}
          </Maplet>
          {selectedFeature && <FeatureDetails
            editing={editing}
            hideFeatureLink={hideFeatureLink}
            noLinks={noLinks}
            urls={details.urls}
            onRequestClose={this.deselectFeature}
            feature={selectedFeature}
            asset={this.getAsset(selectedFeature)}
            metricUnits={metricUnits}
            showUnitConversion={showUnitConversion} />}
          {showDetailsOnHover && !selectedFeature && hoveredFeature && <FeatureDetails
            editing={editing}
            hideFeatureLink
            noLinks={noLinks}
            onRequestClose={this.deselectFeature}
            feature={hoveredFeature}
            asset={this.getAsset(hoveredFeature)}
            metricUnits={metricUnits}
            showUnitConversion={showUnitConversion} />}
          {!hidePoweredBy && poweredBy}
        </div>
        {poiBar && <PoiBar
          poiTags={details.poiTags}
          selectedTags={enabledPoiTags}
          toggleTag={this.onTogglePoiTag} />}
      </div>
    )
  }
}

const poweredBy = (
  <div className={styles.poweredBy}>
    <a target='_blank' href='https://ridewithgps.com'>
      <span>Powered by </span><img src='/images/logos/horizontal-logo-white.svg' width={94} height={22} />
    </a>
  </div>
)
